import React from 'react';
import { compact } from 'lodash';
import { Dictionary } from 'ts-essentials';
import { useSelector } from 'react-redux';
import {
  AssignmentIndOutlinedIcon,
  CallMadeOutlinedIcon,
  CallReceivedOutlinedIcon,
  ErrorOutlineIcon,
  HelpOutlineIcon,
  MoreTimeOutlinedIcon,
  NotesOutlinedIcon,
  SpeakerNotesOffOutlinedIcon,
  SwapHorizOutlinedIcon
} from 'element';
import { RosteredShift, RosteredShiftStatus } from 'type';
import { blue, orange, purple, red } from '../../../../../../lib/styles';
import { CardIconsPropsIcon } from '../../views';
import { getFlag } from 'state/Flags';

const statusConfig: Dictionary<CardIconsPropsIcon | null, RosteredShiftStatus> =
  {
    'offer awaiting approval': {
      title: 'Offer awaiting approval',
      color: orange,
      Icon: CallReceivedOutlinedIcon,
    },
    'swap awaiting approval': {
      title: 'Swap offer awaiting approval',
      color: orange,
      Icon: SwapHorizOutlinedIcon,
    },
    offer: {
      title: 'Offered',
      color: purple,
      Icon: CallMadeOutlinedIcon,
    },
    swap: {
      title: 'Swap offered',
      color: purple,
      Icon: SwapHorizOutlinedIcon,
    },
    deleted: null,
    none: null,
  };

type Props = Pick<
  RosteredShift,
  'status' | 'is_overtime' | 'is_overlap_error' | 'is_accepted' | 'user_id' | 'notify_user' | 'notes'
>;

const useIcons = ({
  status,
  is_overtime,
  is_overlap_error,
  is_accepted,
  user_id,
  notify_user,
  notes
}: Props) => {
  const icon = statusConfig[status];
  const shouldShowOvertime = useSelector(getFlag.overtimeShifts) && is_overtime;
  const shouldShowAwaitingAcceptance = !is_accepted && user_id;

  return React.useMemo(
    () =>
      compact<CardIconsPropsIcon>([
        icon,
        shouldShowOvertime && {
            title: 'Overtime',
            color: blue,
            Icon: MoreTimeOutlinedIcon,
          },
        is_overlap_error && {
          title: 'Shift overlaps',
          color: red,
          Icon: ErrorOutlineIcon,
        },
        shouldShowAwaitingAcceptance && {
            title: 'Awaiting acceptance',
            color: orange,
            Icon: AssignmentIndOutlinedIcon,
          },
        !notify_user && {
          title: 'Not notified',
          color: red,
          Icon: SpeakerNotesOffOutlinedIcon,
        },
        !user_id && {
          title: 'Unassigned',
          color: orange,
          Icon: HelpOutlineIcon,
        },
        notes && {
          title: 'Notes',
          color: red,
          Icon: NotesOutlinedIcon
        }
      ]),
    [
      icon,
      is_overlap_error,
      user_id,
      notify_user,
      notes,
      shouldShowAwaitingAcceptance,
      shouldShowOvertime
    ]
  );
};

export default useIcons;
